import type {
  AssistantProfileChoice,
  AssistantProfileEffectiveSelection,
  AssistantProfileProcessingDisclosure,
  AssistantProfileSelection
} from "./AssistantProfileDisclosure";

export type AssistantProfileDisclosureModel = {
  profiles: readonly AssistantProfileChoice[];
  knowledgeScopes: readonly AssistantProfileChoice[];
  languages: readonly AssistantProfileChoice[];
  compositions: readonly AssistantProfileChoice[];
  effectiveSelection?: AssistantProfileEffectiveSelection;
  localProcessing: AssistantProfileProcessingDisclosure;
};

export type AssistantProfileSelectionHint = {
  mode: "auto" | "manual";
  profile_id?: string;
  scope_ids: string[];
  language?: string;
  composition?: string;
};

const REMEMBERED_SELECTION_STORAGE_KEY = "sellpath.assistant-profile.selection.v1";

const localOnlyUnknown: AssistantProfileProcessingDisclosure = {
  scope: "local_only",
  status: "unknown"
};

const recordOf = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
};

const textOf = (value: unknown, maxLength: number): string | undefined => {
  if (typeof value !== "string") return undefined;
  const normalized = value.replace(/\s+/g, " ").trim();
  return normalized && normalized.length <= maxLength ? normalized : undefined;
};

const hasField = (record: Record<string, unknown>, field: string): boolean =>
  Object.prototype.hasOwnProperty.call(record, field);

const choiceListOf = (value: unknown): AssistantProfileChoice[] => {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  return value.reduce<AssistantProfileChoice[]>((choices, item) => {
    const record = recordOf(item);
    const choiceValue = textOf(record?.value, 256);
    const label = textOf(record?.label, 160);
    if (!choiceValue || !label || seen.has(choiceValue)) return choices;
    seen.add(choiceValue);
    choices.push({
      value: choiceValue,
      label,
      ...(record?.disabled === true ? { disabled: true } : {}),
      ...(textOf(record?.disabled_reason, 240) ? { disabledReason: textOf(record?.disabled_reason, 240) } : {})
    });
    return choices;
  }, []);
};

const enabledValues = (choices: readonly AssistantProfileChoice[]): Set<string> =>
  new Set(choices.filter((choice) => !choice.disabled).map((choice) => choice.value));

const valueOf = (value: unknown, choices: readonly AssistantProfileChoice[]): string | undefined => {
  const candidate = textOf(value, 256);
  return candidate && enabledValues(choices).has(candidate) ? candidate : undefined;
};

const scopeIdsOf = (value: unknown, choices: readonly AssistantProfileChoice[]): string[] => {
  if (!Array.isArray(value)) return [];
  const allowed = enabledValues(choices);
  return [
    ...new Set(
      value.map((item) => textOf(item, 256)).filter((item): item is string => Boolean(item && allowed.has(item)))
    )
  ];
};

const selectionMethodOf = (value: unknown): AssistantProfileEffectiveSelection["selectionMethod"] => {
  const methods = ["manual", "saved_preference", "department_default", "agent_routed", "safe_fallback"] as const;
  return methods.includes(value as (typeof methods)[number]) ? (value as (typeof methods)[number]) : undefined;
};

const effectiveSelectionOf = (
  value: unknown,
  profiles: readonly AssistantProfileChoice[],
  knowledgeScopes: readonly AssistantProfileChoice[],
  languages: readonly AssistantProfileChoice[],
  compositions: readonly AssistantProfileChoice[]
): AssistantProfileEffectiveSelection | undefined => {
  const record = recordOf(value);
  if (!record || (record.mode !== "auto" && record.mode !== "manual")) return undefined;
  return {
    mode: record.mode,
    profileId: valueOf(record.profile_id, profiles),
    scopeIds: scopeIdsOf(record.scope_ids, knowledgeScopes),
    language: valueOf(record.language, languages),
    composition: valueOf(record.composition, compositions),
    selectionMethod: selectionMethodOf(record.selection_method)
  };
};

const processingOf = (value: unknown): AssistantProfileProcessingDisclosure => {
  const record = recordOf(value);
  const scope = record?.scope === "external_allowed" || record?.scope === "local_only" ? record.scope : "local_only";
  const status = ["ready", "fallback", "unavailable", "unknown"].includes(String(record?.status))
    ? (record?.status as AssistantProfileProcessingDisclosure["status"])
    : "unknown";
  return { scope, status };
};

const emptyDisclosureModel = (): AssistantProfileDisclosureModel => ({
  profiles: [],
  knowledgeScopes: [],
  languages: [],
  compositions: [],
  localProcessing: localOnlyUnknown
});

/**
 * The current page-chat response has no Assistant Profile contract yet. The
 * narrow seam reserved for it is a server-owned `assistant_profile` envelope
 * on a BOT chat bubble's `additional_data` (or on the response content).
 * Missing or malformed data deliberately leaves the UI with no manual choices
 * and a fail-closed processing disclosure.
 */
export const parseAssistantProfileDisclosure = (value: unknown): AssistantProfileDisclosureModel => {
  const record = recordOf(value);
  if (!record) return emptyDisclosureModel();

  const profiles = choiceListOf(record.profiles);
  const knowledgeScopes = choiceListOf(record.knowledge_scopes);
  const languages = choiceListOf(record.languages);
  const compositions = choiceListOf(record.compositions);
  return {
    profiles,
    knowledgeScopes,
    languages,
    compositions,
    effectiveSelection: effectiveSelectionOf(
      record.effective_selection,
      profiles,
      knowledgeScopes,
      languages,
      compositions
    ),
    localProcessing: processingOf(record.local_processing)
  };
};

const assistantSender = (record: Record<string, unknown>): boolean => {
  const sender = String(record.sender || record.sender_type || "").toLowerCase();
  return sender === "assistant" || sender === "bot";
};

/** Only server response records are eligible; user-authored metadata is ignored. */
export const findAssistantProfileDisclosure = (value: unknown): { found: boolean; value?: unknown } => {
  const record = recordOf(value);
  if (!record) return { found: false };
  const hasSender = hasField(record, "sender") || hasField(record, "sender_type");
  const directEnvelopeAllowed = !hasSender || assistantSender(record);
  if (directEnvelopeAllowed && hasField(record, "assistant_profile")) {
    return { found: true, value: record.assistant_profile };
  }

  const additionalData = recordOf(record.additional_data);
  if (directEnvelopeAllowed && additionalData && hasField(additionalData, "assistant_profile")) {
    return { found: true, value: additionalData.assistant_profile };
  }

  const history = Array.isArray(record.chat_history) ? record.chat_history : [record.chat_history];
  for (const item of history) {
    const chat = recordOf(item);
    if (!chat || !assistantSender(chat)) continue;
    const chatAdditionalData = recordOf(chat.additional_data);
    if (chatAdditionalData && hasField(chatAdditionalData, "assistant_profile")) {
      return { found: true, value: chatAdditionalData.assistant_profile };
    }
  }
  return { found: false };
};

const safeSelectionValue = (value: unknown): string | undefined => textOf(value, 256);

export const toAssistantProfileSelectionHint = (
  selection: AssistantProfileSelection
): AssistantProfileSelectionHint => ({
  mode: selection.mode === "manual" ? "manual" : "auto",
  ...(safeSelectionValue(selection.profileId) ? { profile_id: safeSelectionValue(selection.profileId) } : {}),
  scope_ids: [...new Set(selection.scopeIds.map(safeSelectionValue).filter((item): item is string => Boolean(item)))],
  ...(safeSelectionValue(selection.language) ? { language: safeSelectionValue(selection.language) } : {}),
  ...(safeSelectionValue(selection.composition) ? { composition: safeSelectionValue(selection.composition) } : {})
});

export const autoAssistantProfileSelection = (): AssistantProfileSelection => ({ mode: "auto", scopeIds: [] });

const selectionOf = (value: unknown): AssistantProfileSelection => {
  const record = recordOf(value);
  if (!record) return autoAssistantProfileSelection();
  const hint = toAssistantProfileSelectionHint({
    mode: record.mode === "manual" ? "manual" : "auto",
    profileId: safeSelectionValue(record.profileId) || safeSelectionValue(record.profile_id),
    scopeIds: Array.isArray(record.scopeIds)
      ? record.scopeIds.filter((item): item is string => typeof item === "string")
      : Array.isArray(record.scope_ids)
        ? record.scope_ids.filter((item): item is string => typeof item === "string")
        : [],
    language: safeSelectionValue(record.language),
    composition: safeSelectionValue(record.composition)
  });
  return {
    mode: hint.mode,
    profileId: hint.profile_id,
    scopeIds: hint.scope_ids,
    language: hint.language,
    composition: hint.composition
  };
};

export const readRememberedAssistantProfileSelection = (): AssistantProfileSelection => {
  if (typeof window === "undefined") return autoAssistantProfileSelection();
  try {
    const raw = window.localStorage.getItem(REMEMBERED_SELECTION_STORAGE_KEY);
    return raw ? selectionOf(JSON.parse(raw)) : autoAssistantProfileSelection();
  } catch {
    return autoAssistantProfileSelection();
  }
};

export const rememberAssistantProfileSelection = (selection: AssistantProfileSelection): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      REMEMBERED_SELECTION_STORAGE_KEY,
      JSON.stringify(toAssistantProfileSelectionHint(selection))
    );
  } catch {
    // A storage failure must not prevent the current chat preference from applying.
  }
};
